import React, { useState, useContext } from "react";
import { useForm, Controller } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import * as yup from "yup";
import { AiFillCloseCircle } from "react-icons/ai";
import InputLabel from "@mui/material/InputLabel";
import MenuItem from "@mui/material/MenuItem";
import FormControl from "@mui/material/FormControl";
import Select from "@mui/material/Select";
import TextField from "@mui/material/TextField";
import InputUI from "../material-ui/InputUI";
import ButtonUI from "../material-ui/ButtonUI";
import { AuthContext } from "../../context/Auth/AuthContext";
import updateEmailTemplateData from "../../api/updateEmailTemplateData";
import { showSuccess, showError } from "../../utils/toast";
import addTemplateImg from "../../images/add-user.png";
import "../../styles/AddDocument.css";
import {
  CHASING_TYPE,
  NON_CHASING_TYPES,
  isChasingTemplateType,
  getEmailTemplateTypeOptions,
  emailTemplateLanguageOptions,
  CHASING_PLACEHOLDERS_HELP,
  NON_CHASING_PLACEHOLDERS_HELP,
} from "../../utils/emailTemplateOptions";

const editEmailTemplateSchema = yup.object().shape({
  name: yup.string().required("Template Name is required"),
  type: yup.string().required("Type is required"),
  subject: yup.string().required("Subject is required"),
  body_html: yup.string().required("Body HTML is required"),
  status: yup.string().required("Status is required"),
  language: yup.string().nullable().when("type", {
    is: (type) => NON_CHASING_TYPES.includes(type),
    then: yup.string().required("Language is required"),
  }),
});

// Full-page Edit view — mirrors AddEmailTemplate.jsx's layout exactly so the
// module has one consistent pattern for both Add and Edit (matching the
// module's own established full-page convention), instead of Edit opening
// as a separate popup.
const EditEmailTemplate = ({
  tableValues,
  onEditButtonClick,
  setEmailTemplatesAddedToTrue,
}) => {
  const [loading, setLoading] = useState(false);
  const { token, user_id } = useContext(AuthContext);

  const {
    register,
    handleSubmit,
    control,
    watch,
    formState: { errors },
  } = useForm({
    resolver: yupResolver(editEmailTemplateSchema),
    defaultValues: {
      name: tableValues?.name || "",
      type: tableValues?.type || "document_chasing",
      subject: tableValues?.subject || "",
      body_html: tableValues?.body_html || "",
      description: tableValues?.description || "",
      language: tableValues?.language || "",
      status: tableValues?.status || "active",
    },
  });

  const statusOptions = [
    { name: "Active", value: "active" },
    { name: "Inactive", value: "inactive" },
  ];

  const typeOptions = getEmailTemplateTypeOptions(tableValues?.type);
  const isChasingType = isChasingTemplateType(watch("type") || CHASING_TYPE);

  const onFormSubmit = async (formData) => {
    setLoading(true);

    const response = await updateEmailTemplateData({
      token: token,
      userId: user_id,
      id: tableValues.id,
      name: formData.name,
      description: formData.description || "",
      type: formData.type,
      language: isChasingType ? "" : formData.language,
      subject: formData.subject,
      body_html: formData.body_html,
      status: formData.status,
    });

    if (response?.code === 200) {
      onEditButtonClick();
      setEmailTemplatesAddedToTrue();
      showSuccess("Email Template updated successfully");
    } else {
      showError("Error while updating Email Template");
    }
    setLoading(false);
  };

  return (
    <div className="add-document-page">
      <div className="add-document-container">
        <div className="add-document-header">
          <div className="add-document-title-with-img">
            <img
              src={addTemplateImg}
              height="100px"
              width="120px"
              alt="Edit Email Template"
            />
            <div className="add-document-title">Edit Email Template</div>
          </div>
          <ButtonUI
            formButton={false}
            variant="contained"
            buttonText={<AiFillCloseCircle />}
            style={{
              background: "none",
              padding: "0px",
              minWidth: "auto",
            }}
            onClickFunc={onEditButtonClick}
          ></ButtonUI>
        </div>

        <div className="add-document-form">
          <div className="add-document-inputs">
            <div className="add-document-inputs-with-error-message">
              <InputUI
                name="name"
                label="Template Name"
                type="text"
                variant="outlined"
                cssStyles={{ margin: "10px 0px" }}
                register={register}
              />
              <span className="error-message">{errors.name?.message}</span>
            </div>
            <div className="add-document-inputs-with-error-message select-ui">
              <FormControl fullWidth>
                <InputLabel id="edit-type-label">Type</InputLabel>
                <Controller
                  control={control}
                  name="type"
                  render={({ field: { onChange, value } }) => (
                    <Select
                      sx={{ margin: "10px 0px" }}
                      labelId="edit-type-label"
                      variant="outlined"
                      value={value || "document_chasing"}
                      onChange={onChange}
                      label="Type"
                    >
                      {typeOptions.map((option) => (
                        <MenuItem key={option.value} value={option.value}>
                          {option.name}
                        </MenuItem>
                      ))}
                    </Select>
                  )}
                />
              </FormControl>
              <span className="error-message">{errors.type?.message}</span>
            </div>
          </div>

          {!isChasingType && (
            <div className="add-document-inputs">
              <div className="add-document-inputs-with-error-message select-ui">
                <FormControl fullWidth>
                  <InputLabel id="edit-language-label">Language</InputLabel>
                  <Controller
                    control={control}
                    name="language"
                    render={({ field: { onChange, value } }) => (
                      <Select
                        sx={{ margin: "10px 0px" }}
                        labelId="edit-language-label"
                        variant="outlined"
                        value={value || ""}
                        onChange={onChange}
                        label="Language"
                      >
                        {emailTemplateLanguageOptions.map((option) => (
                          <MenuItem key={option.value} value={option.value}>
                            {option.name}
                          </MenuItem>
                        ))}
                      </Select>
                    )}
                  />
                </FormControl>
                <span className="error-message">
                  {errors.language?.message}
                </span>
              </div>
            </div>
          )}

          <div className="add-document-inputs">
            <div
              className="add-document-inputs-with-error-message"
              style={{ width: "100%" }}
            >
              <InputUI
                name="subject"
                label="Subject Line"
                type="text"
                variant="outlined"
                cssStyles={{ margin: "10px 0px" }}
                register={register}
              />
              <span className="error-message">{errors.subject?.message}</span>
            </div>
          </div>

          <div className="add-document-inputs">
            <div
              className="add-document-inputs-with-error-message"
              style={{ width: "100%" }}
            >
              <Controller
                control={control}
                name="body_html"
                render={({ field }) => (
                  <TextField
                    {...field}
                    label="Body HTML"
                    variant="outlined"
                    fullWidth
                    multiline
                    rows={8}
                    sx={{ margin: "10px 0px" }}
                    error={!!errors.body_html}
                    helperText={
                      errors.body_html?.message ||
                      (isChasingType
                        ? CHASING_PLACEHOLDERS_HELP
                        : NON_CHASING_PLACEHOLDERS_HELP)
                    }
                  />
                )}
              />
            </div>
          </div>

          <div className="add-document-inputs">
            <div className="add-document-inputs-with-error-message">
              <InputUI
                name="description"
                label="Description (Optional)"
                type="text"
                variant="outlined"
                cssStyles={{ margin: "10px 0px" }}
                register={register}
              />
            </div>
            <div className="add-document-inputs-with-error-message select-ui">
              <FormControl fullWidth>
                <InputLabel id="edit-status-label">Status</InputLabel>
                <Controller
                  control={control}
                  name="status"
                  render={({ field: { onChange, value } }) => (
                    <Select
                      sx={{ margin: "10px 0px" }}
                      labelId="edit-status-label"
                      variant="outlined"
                      value={value || "active"}
                      onChange={onChange}
                      label="Status"
                    >
                      {statusOptions.map((option) => (
                        <MenuItem key={option.value} value={option.value}>
                          {option.name}
                        </MenuItem>
                      ))}
                    </Select>
                  )}
                />
              </FormControl>
              <span className="error-message">{errors.status?.message}</span>
            </div>
          </div>

          <div className="add-document-button">
            <ButtonUI
              loading={loading}
              variant="contained"
              buttonText="Update Email Template"
              style={{
                width: "220px",
                height: "40px",
                backgroundColor: "#1a6b7c",
                boxShadow: "none",
                border: "1px solid #1a6b7c",
                fontWeight: "500",
                borderRadius: "4px",
                letterSpacing: "0.07rem",
                marginTop: "20px",
              }}
              handleSubmit={handleSubmit}
              onFormSubmit={onFormSubmit}
            />
          </div>
        </div>
      </div>
    </div>
  );
};

export default EditEmailTemplate;
